import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { List, NumberedList, ListItem } from '@fiverr-private/list';
import { Link } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { Collapsible, CollapsibleHeader, CollapsibleContent } from '@fiverr-private/disclosure';
import { translate } from '@fiverr-private/i18n-react';
import { useEditorAsyncProps } from '../listings/hooks/useEditorAsyncProps';
import { stringToLink } from '../../../stringToLink';

import './styles.scss';

interface TocLink {
    id: string;
    text: string;
    link: string;
    childrenIds?: string[];
}

interface TableOfContentsProps {
    h2Links?: TocLink[];
    h3Links?: TocLink[];
    h2?: boolean;
    h3?: boolean;
    isNumbered: boolean;
    defaultExpanded?: boolean;
    usePageHeadings?: boolean;
}

const TableOfContentsContainer = ({ children }: { children: React.ReactNode }) => (
    <Box className="section-table-of-contents" backgroundColor="blue_100">
        {children}
    </Box>
);

const PAGE_CONTENT_SELECTOR = '.builder-component';

const TableOfContents = ({
    isNumbered,
    defaultExpanded = false,
    usePageHeadings = false,
    h2,
    h3,
    ...props
}: TableOfContentsProps) => {
    const [h2Links, setH2Links] = useState(props.h2Links || []);
    const [h3Links, setH3Links] = useState(props.h3Links || []);

    const editorAsyncProps: Record<string, any> = useEditorAsyncProps('TableOfContents');

    useEffect(() => {
        if (usePageHeadings) return;
        if (!isEmpty(editorAsyncProps?.h2Links)) {
            setH2Links(editorAsyncProps.h2Links);
        }
        if (!isEmpty(editorAsyncProps?.h3Links)) {
            setH3Links(editorAsyncProps.h3Links);
        }
    }, [editorAsyncProps, usePageHeadings]);

    useEffect(() => {
        if (!usePageHeadings) return;

        setH2Links([]);
        setH3Links([]);

        const tags = [h2 && 'h2', h3 !== false && 'h3'].filter(Boolean).join(',');
        if (!tags) return;

        const body = document.querySelector(PAGE_CONTENT_SELECTOR);
        if (!body) return;

        const headings = Array.from(body.querySelectorAll<HTMLElement>(tags));
        if (!headings.length) return;

        const newH2: TocLink[] = [];
        const newH3: TocLink[] = [];
        headings.forEach((el) => {
            const text = el.textContent?.trim() ?? '';
            const id = el.id || stringToLink(text);
            if (!el.id) el.id = id;
            if (el.tagName === 'H2') {
                newH2.push({ id, text, link: `#${id}`, childrenIds: [] });
            } else {
                newH3.push({ id, text, link: `#${id}` });
                if (newH2.length) newH2[newH2.length - 1].childrenIds!.push(id);
            }
        });
        setH2Links(newH2);
        setH3Links(newH3);
    }, [usePageHeadings, h2, h3]);

    const ListTag = isNumbered ? NumberedList : List;

    const mapIdsToLinks = (ids: string[], links: TocLink[]): TocLink[] =>
        ids.map((id) => links.find((link) => link.id === id)).filter(Boolean) as TocLink[];

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
        e.preventDefault();
        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
        window.history.pushState(null, '', `#${targetId}`);
    };

    const renderLinks = (links: TocLink[], childrenLinks?: TocLink[], withPadding?: boolean) => (
        <ListTag paddingLeft={isNumbered || withPadding ? '5' : '0'}>
            {links?.map((heading, idx) => (
                <React.Fragment key={idx}>
                    <ListItem>
                        <Link
                            color={{ hover: 'blue_700' }}
                            lineHeight={{ default: 'b_md' }}
                            href={heading.link}
                            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleLinkClick(e, heading.id)}
                        >
                            {heading.text}
                        </Link>
                        {!!heading.childrenIds?.length &&
                            !!childrenLinks?.length &&
                            renderLinks(mapIdsToLinks(heading.childrenIds, childrenLinks), [], true)}
                    </ListItem>
                </React.Fragment>
            ))}
        </ListTag>
    );

    if (!h2Links.length && !h3Links.length) return null;

    const linkList = h2Links.length ? renderLinks(h2Links, h3Links) : renderLinks(h3Links);

    return (
        <TableOfContentsContainer>
            <Collapsible defaultExpanded={defaultExpanded}>
                <CollapsibleHeader>{translate<string>('builder_components.table_of_contents.title')}</CollapsibleHeader>
                <CollapsibleContent>{linkList}</CollapsibleContent>
            </Collapsible>
        </TableOfContentsContainer>
    );
};

export default TableOfContents;
